import React, {MouseEventHandler, useState} from 'react'
import {EmojiIcon} from 'ladder/agents/dashboard/uxcommon2'
import DropdownIcon from 'Images/dropdown-icon.svg'
import ZippedFileIcon from 'Images/agents/zipped-file-icon.svg'
import RedirectIcon from 'Images/agents/redirect-icon.svg'
import DocumentIcon from 'Images/agents/document-icon.svg'
import {Text} from 'ladder/ux/typography2'
import './learn2.css'
import {staticUrl} from 'ladder/build2'

export function SettingsPage2({onNavigate}) {
  return <div className='agent-settings-page'>
    <div className='settings-row' onClick={() => onNavigate('settings/profile')}>
      <EmojiIcon backgroundColor='#F3D7AA' emoji='😊'/>
      <Text size='body-15' weight='600'>Profile</Text>
    </div>
    <div className='settings-row' onClick={() => onNavigate('settings/support')}>
      <EmojiIcon backgroundColor='#AFCEF5' emoji='❤️'/>
      <Text size='body-15' weight='600'>Support</Text>
    </div>
    <div className='settings-row' onClick={() => window.location.replace('/logout')}>
      <EmojiIcon backgroundColor='#F7A5BD' emoji='🚪'/>
      <Text size='body-15' weight='600'>Log out</Text>
    </div>
  </div>
}

const listHierarchy = {
  'Underwriting guide': [
    'Ladder underwriting guidelines',
  ],
  'Agent resources': [
    'Why Ladder is different',
    'Agent training deck',
    'Ladder for Agents overview',
    'What Ladder provides',
    'Getting started guide',
    'Conversation starters',
    'Addressing common questions',
    'Compliance manual',
    'FAQs',
  ],
  'Client-facing resources': [
    'Introduction to Ladder',
    'Who Ladder is',
    'Guide to life insurance',
    'Term versus other insurance types',
    'Answers to common questions',
    'Why choose Ladder?',
    'Coverage calculator',
    'Introduction to Laddering',
    'Laddering & flexible coverage',
    'Life insurance & homeownership',
  ],
  'Marketing Guide': [
    'Social media assets',
  ],
}

type RowId = keyof (typeof listHierarchy)

interface StaticResourceContentItemData {
  icon: 'document' | 'zip';
  resourcePath: string;
}

interface RouteLinkContentItemData {
  icon: 'redirect';
  routeLink: string;
}

type ContentItemData = StaticResourceContentItemData | RouteLinkContentItemData

const contentItems: Record<string, ContentItemData> = {
  'Ladder underwriting guidelines': {
    icon: 'document', resourcePath: '/pdfs/agents/general-underwriting-guidelines.pdf',
  },
  'Why Ladder is different': {
    icon: 'document', resourcePath: '/pdfs/agents/why-ladder-is-different.pdf',
  },
  'Agent training deck': {
    icon: 'document', resourcePath: '/ppt/agent-training-deck.pptx',
  },
  'Ladder for Agents overview': {
    icon: 'document', resourcePath: '/pdfs/agents/ladder-for-agents-overview.pdf',
  },
  'What Ladder provides': {
    icon: 'document', resourcePath: '/pdfs/agents/promise-to-provide.pdf',
  },
  'Getting started guide': {
    icon: 'document', resourcePath: '/pdfs/agents/getting-started-guide.pdf',
  },
  'Conversation starters': {
    icon: 'document', resourcePath: '/pdfs/agents/email-text-message-scripts.pdf',
  },
  'Addressing common questions': {
    icon: 'document', resourcePath: '/pdfs/agents/common-customer-questions.pdf',
  },
  'Compliance manual': {
    icon: 'document', resourcePath: '/pdfs/agents/ladder-agent-compliance-manual.pdf',
  },
  'FAQs': {
    icon: 'redirect', routeLink: '/agents/faq',
  },
  'Introduction to Ladder': {
    icon: 'document', resourcePath: '/pdfs/agents/ladder-for-clients.pdf',
  },
  'Who Ladder is': {
    icon: 'redirect', routeLink: '/about',
  },
  'Guide to life insurance': {
    icon: 'redirect', routeLink: '/guide',
  },
  'Term versus other insurance types': {
    icon: 'document', resourcePath: '/pdfs/agents/term-versus-other-life-insurance-types.pdf',
  },
  'Answers to common questions': {
    icon: 'document', resourcePath: '/pdfs/agents/life-insurance-essential-questions.pdf',
  },
  'Why choose Ladder?': {
    icon: 'document', resourcePath: '/pdfs/agents/why-choose-ladder.pdf',
  },
  'Coverage calculator': {
    icon: 'redirect', routeLink: '/calculator',
  },
  'Introduction to Laddering': {
    icon: 'document', resourcePath: '/pdfs/agents/introduction-to-laddering.pdf',
  },
  'Laddering & flexible coverage': {
    icon: 'redirect', routeLink: '/laddering',
  },
  'Life insurance & homeownership': {
    icon: 'document', resourcePath: '/pdfs/agents/life-insurance-and-homeownership.pdf',
  },
  'Social media assets': {
    icon: 'zip', resourcePath: '/zips/social-ads-for-agents.zip',
  },
}

interface HeadingItemData {
  emoji: string;
  color: string;
  subheading?: string;
}

const headingItems: Record<RowId, HeadingItemData> = {
  'Underwriting guide': {
    emoji: '🫁',
    color: '#EFC6D2',
  },
  'Agent resources': {
    emoji: '📕',
    color: '#AFCEF5',
    subheading: 'Learn about Ladder and term life',
  },
  'Client-facing resources': {
    emoji: '🎓',
    color: '#F3D7AA',
    subheading: 'Send clients pre-designed materials',
  },
  'Marketing Guide': {
    emoji: '📣',
    color: '#EFC6D2',
    subheading: 'Download social media materials',
  },
}

function HeadingListItem({onClick, rowId}: { rowId: RowId, onClick: MouseEventHandler }) {
  const {color, emoji, subheading} = headingItems[rowId]
  return <div className='dropdown-listitem heading-item'
              onClick={onClick}>
    <div className='circle-icon' style={{backgroundColor: color}}>
      {emoji}
    </div>
    <div className='heading-text'>
      <Text block size='header-18' weight='300'>{rowId}</Text>
      <Text size='body-12' color='gray'>{subheading}</Text>
    </div>
    <div className='dropdown-icon'>
      <DropdownIcon/>
    </div>
  </div>
}

function ContentListItem({itemText}: { itemText: string }) {
  const {icon, ...props} = contentItems[itemText]
  const href = 'resourcePath' in props ? staticUrl(props.resourcePath) : props.routeLink
  const Icon = {
    document: DocumentIcon,
    redirect: RedirectIcon,
    zip: ZippedFileIcon,
  }[icon]
  return <div className='dropdown-listitem content-item'>
    <a href={href}>
      <Icon style={{margin: '7px'}}/>
    </a>
    <a className='text-link' href={href}>
      <Text size='body-13' weight='bold'>{itemText}</Text>
    </a>
  </div>
}

function ResourcesDropdownList({rowId, toggleExpanded, expandedRows}: {
  rowId: RowId,
  toggleExpanded: Function,
  expandedRows: Set<RowId>
}) {
  return <div className='dropdown-list'>
    <HeadingListItem rowId={rowId} onClick={() => toggleExpanded(rowId)}/>
    {expandedRows.has(rowId) && listHierarchy[rowId].map(itemText => (
      <ContentListItem itemText={itemText}/>
    ))}
  </div>
}

export function LearnPage2() {
  const [expandedRows, setExpandedRows]: [Set<RowId>, Function] = useState(new Set())
  const toggleExpanded = (rowId: RowId) => {
    const newRows = new Set(expandedRows)
    if (expandedRows.has(rowId)) {
      newRows.delete(rowId)
    } else {
      newRows.add(rowId)
    }
    setExpandedRows(newRows)
  }
  return <div className='agent-resources-page'>
    <ResourcesDropdownList rowId='Underwriting guide' {...{toggleExpanded, expandedRows}}/>
    <ResourcesDropdownList rowId='Agent resources' {...{toggleExpanded, expandedRows}}/>
    <ResourcesDropdownList rowId='Client-facing resources' {...{toggleExpanded, expandedRows}}/>
    <ResourcesDropdownList rowId='Marketing Guide' {...{toggleExpanded, expandedRows}}/>
  </div>
}
